// Temporary workaround. Resolve ASAP. Do not add more ignores like this.
// eslint-disable-next-line eslint-comments/no-restricted-disable
// eslint-disable-next-line @nx/enforce-module-boundaries
import { BuyButtonPosition } from '@marketplace-web/messaging/conversation-messaging-data'
import { TrackingEvent } from '@marketplace-web/observability/event-tracker-data'

// Source: https://github.com/vinted/dwh-schema-registry/blob/5387d2ce7b44ee93711a15dfb97c60a4d984f22c/avro/events/common/shared/user.avdl#L25
type UserTarget = 'select_suggested_discount'

type startConversationEventArgs = {
  userId: number
  itemId: number | string | null | undefined
}

export const startConversationEvent = (args: startConversationEventArgs): TrackingEvent => {
  const { itemId, userId } = args

  const extra = {
    user_id: userId,
    item_id: itemId,
  }

  return {
    event: 'user.start_conversation',
    extra,
  }
}

type SellerMakeOfferStartEventArgs = {
  offerSessionId: string
  hasSuggestedPrices: boolean
  currencyCode: string
  currentPrice: number
  transactionId?: string
}

type SellerMakeOfferStartEventExtra = {
  offer_session_id: string
  has_suggested_prices: boolean
  currency: string
  current_price: number
  transaction_id?: string
}

export const sellerMakeOfferStartEvent = (args: SellerMakeOfferStartEventArgs) => {
  const { offerSessionId, hasSuggestedPrices, currencyCode, currentPrice, transactionId } = args

  const extra: SellerMakeOfferStartEventExtra = {
    offer_session_id: offerSessionId,
    has_suggested_prices: hasSuggestedPrices,
    currency: currencyCode,
    current_price: currentPrice,
    transaction_id: transactionId,
  }

  return {
    event: 'seller.make_offer_start',
    extra,
  }
}

type StartMakeOfferRequestEventArgs = {
  offerRequestSessionId: string
  hasSuggestedPrices: boolean
  currentPrice: number
  transactionId?: string
}

type StartMakeOfferRequestEventExtra = {
  offer_request_session_id: string
  has_suggested_prices: boolean
  current_price: number
  transaction_id?: string
}

export const startMakeOfferRequestEvent = (args: StartMakeOfferRequestEventArgs) => {
  const { offerRequestSessionId, hasSuggestedPrices, currentPrice, transactionId } = args

  const extra: StartMakeOfferRequestEventExtra = {
    offer_request_session_id: offerRequestSessionId,
    has_suggested_prices: hasSuggestedPrices,
    current_price: currentPrice,
    transaction_id: transactionId,
  }

  return {
    event: 'buyer.make_offer_request_start',
    extra,
  }
}

type MakeOfferEventArgs = {
  isBuyer: boolean
  transactionId: number
  offerRequestId?: number
  currentPrice: number
  offeredPrice: number
  offerRequestSessionId?: string
  offerSuggestionOptionNumber: number | null
  offerSessionId?: string
  pendingSellerOfferId?: number
}

type MakeOfferEventExtra = {
  transaction_id: number
  offer_id?: number
  current_price: number
  offered_price: number
  button_number: number | null
  offer_request_session_id?: string
  offer_session_id?: string
  pending_seller_offer_id?: number
}

export const makeOfferEvent = (args: MakeOfferEventArgs) => {
  const {
    isBuyer,
    currentPrice,
    offeredPrice,
    transactionId,
    offerRequestId,
    offerRequestSessionId,
    offerSuggestionOptionNumber,
    offerSessionId,
    pendingSellerOfferId,
  } = args

  const extra: MakeOfferEventExtra = {
    transaction_id: transactionId,
    offer_id: offerRequestId,
    offer_request_session_id: offerRequestSessionId,
    current_price: currentPrice,
    offered_price: offeredPrice,
    button_number: offerSuggestionOptionNumber,
    ...(offerSessionId !== undefined && { offer_session_id: offerSessionId }),
    ...(pendingSellerOfferId !== undefined && { pending_seller_offer_id: pendingSellerOfferId }),
  }

  return {
    event: isBuyer ? 'buyer.make_offer_request' : 'seller.make_offer',
    extra,
  }
}

type ClickEventArgs = {
  target: UserTarget
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: UserTarget
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

export const clickEvent = (args: ClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}

type ViewScreenEventArgs = {
  screen: string
}

export const viewScreenEvent = (args: ViewScreenEventArgs): TrackingEvent => {
  const { screen } = args

  const extra = {
    screen,
  }

  return {
    event: 'user.view_screen',
    extra,
  }
}

type WantItemButtonExtra =
  | 'message'
  | 'buy'
  | 'item_reservation_request'
  | 'make_offer'
  | 'create_bundle'

type BuyerWantItemEventExtra = {
  item_id: number | null
  transaction_id: number | null
  button: WantItemButtonExtra
  global_search_session_id: string | null
  global_catalog_browse_session_id?: string | null
  search_session_id: string | null
  homepage_session_id?: string | null
}

type BuyerWantItemEventArgs = {
  transactionId: number | null
  itemId: number | null
  button: WantItemButtonExtra
  globalSearchSessionId: string | null
  globalCatalogBrowseSessionId?: string | null
  searchSessionId: string | null
  homepageSessionId?: string | null
}

export const buyerWantItemEvent = ({
  transactionId,
  itemId,
  button,
  globalSearchSessionId,
  globalCatalogBrowseSessionId,
  searchSessionId,
  homepageSessionId,
}: BuyerWantItemEventArgs): TrackingEvent => {
  const extra: BuyerWantItemEventExtra = {
    button,
    item_id: itemId,
    transaction_id: transactionId,
    global_search_session_id: globalSearchSessionId,
    global_catalog_browse_session_id: globalCatalogBrowseSessionId || undefined,
    search_session_id: searchSessionId,
    homepage_session_id: homepageSessionId,
  }

  return {
    event: 'buyer.want_item',
    extra,
  }
}

type BuyerBuyEventArgs = {
  transactionId: number
  screen: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string | null
  sellerOfferId?: number
  buttonPosition?: BuyButtonPosition
}

type BuyerBuyEventExtra = {
  transaction_id: number
  screen: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  homepage_session_id?: string | null
  seller_offer_id?: number
  button_position?: BuyButtonPosition
}

export const buyerBuyEvent = (args: BuyerBuyEventArgs) => {
  const {
    transactionId,
    screen,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    homepageSessionId,
    sellerOfferId,
    buttonPosition,
  } = args

  const extra: BuyerBuyEventExtra = {
    transaction_id: transactionId,
    screen,
    global_search_session_id: globalSearchSessionId,
    global_catalog_browse_session_id: globalCatalogBrowseSessionId,
    homepage_session_id: homepageSessionId,
    seller_offer_id: sellerOfferId,
    button_position: buttonPosition,
  }

  return {
    event: 'buyer.buy',
    extra,
  }
}

type OpenOfferScreenEventArgs = {
  isBuyer: boolean
  currentPrice: number
  currencyCode: string
  openedFrom: string
  transactionId: number
}

type OpenOfferScreenEventExtra = {
  current_price: number
  currency: string
  opened_from: string
  transaction_id: number
}

export const openOfferScreenEvent = (args: OpenOfferScreenEventArgs) => {
  const { isBuyer, currentPrice, currencyCode, openedFrom, transactionId } = args

  const extra: OpenOfferScreenEventExtra = {
    current_price: currentPrice,
    currency: currencyCode,
    opened_from: openedFrom,
    transaction_id: transactionId,
  }

  return {
    event: isBuyer ? 'buyer.open_offer_screen' : 'seller.open_offer_screen',
    extra,
  }
}
